import React, { FormEvent, useEffect, useRef, useState } from "react"

import * as EmailValidator from "email-validator"

import { Input } from "components/core/Input"
import { Loading } from "components/core/Loading"
import {
  subscribeOwnerToMailingList,
  subscribeToMailingList,
} from "services/arriere"
import { segmentIdentify, segmentTrack } from "utils/segmentAnalytics"

import * as S from "./SubscribeForm.styles"

export interface SubscribeFormProps {
  isOwner?: boolean
  source?: string
}

export const SubscribeForm = ({ isOwner, source }: SubscribeFormProps) => {
  const [firstName, setFirstName] = useState<string | undefined>(undefined)
  const [email, setEmail] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [hasErrorFirstName, setHasErrorFirstName] = useState(false)
  const [emailErrorMessage, setEmailErrorMessage] = useState("")
  const [showSuccessfulMessage, setShowSuccessfulMessage] = useState(false)

  const checkErrorMessage = (error: any) => {
    if (/invalid email/i.test(error?.message)) {
      setEmailErrorMessage("Please enter a valid email address")
    }
  }

  const handleSubscribeClick = async (event: FormEvent) => {
    event.preventDefault()

    if (firstName && email && source) {
      setIsLoading(true)
      if (isOwner) {
        try {
          await subscribeOwnerToMailingList({ firstName, email, source })
          setShowSuccessfulMessage(true)
        } catch (e) {
          checkErrorMessage(e)
        }
      } else {
        try {
          const userId = await subscribeToMailingList({
            email,
            firstName,
            signUpSource: "signup_newsletter_footer_blog",
            source,
          })

          segmentIdentify(userId, { email })
          setShowSuccessfulMessage(true)
        } catch (e) {
          checkErrorMessage(e)
        }
      }
      setIsLoading(false)
    }
  }

  const timeout = useRef<number>()

  useEffect(() => {
    if (showSuccessfulMessage) {
      timeout.current = window.setTimeout(() => {
        setShowSuccessfulMessage(false)
      }, 8000)

      return () => window.clearTimeout(timeout.current)
    }
  }, [showSuccessfulMessage])

  useEffect(() => {
    setHasErrorFirstName(firstName !== undefined && !firstName)

    if (email !== undefined && !EmailValidator.validate(email)) {
      setEmailErrorMessage("Please, provide your email")
    } else if (!!emailErrorMessage) {
      setEmailErrorMessage("")
    }
  }, [firstName, email])

  if (isLoading || showSuccessfulMessage) {
    return (
      <S.SuccessMessage show={isLoading || showSuccessfulMessage}>
        {showSuccessfulMessage ? (
          <>
            You’re in! Check your inbox for the <b>$100</b> off code.
          </>
        ) : (
          <Loading />
        )}
      </S.SuccessMessage>
    )
  }

  return (
    <S.Form onSubmit={handleSubscribeClick}>
      <Input
        errorMessage="Please, provide your name"
        hasError={hasErrorFirstName}
        name="firstname"
        placeholder="First Name"
        value={firstName}
        onChange={(e: FormEvent<HTMLInputElement>) =>
          setFirstName(e.currentTarget?.value)
        }
      />
      <Input
        errorMessage={emailErrorMessage}
        hasError={!!emailErrorMessage}
        name="email"
        placeholder="Email"
        type={"email"}
        value={email}
        onChange={(e: FormEvent<HTMLInputElement>) =>
          setEmail(e.currentTarget?.value)
        }
      />
      <S.Button
        disabled={isLoading || !firstName || !email}
        type="submit"
        onClick={() => {
          segmentTrack("signedUp", {
            category: "signup.form",
            creationDate: new Date(),
            email,
            firstName,
            referralSource: "signup_newsletter_footer_blog",
          })
        }}
      >
        Sign Up
      </S.Button>
    </S.Form>
  )
}
